<template>
  <div class="ai-disease">
    <s-zlifepet-info-doenca :disease="diseaseAi.disease" :visible="showDisease"
      @close="showDisease = false"></s-zlifepet-info-doenca>
    <strong class="disease-name selected-disease-name" v-if="selected">{{
        diseaseAi.disease.name
      }}</strong>
    <div v-else class="tag probability" :data-probability="probabilidade">
      {{ probabilidade }} ({{ diseaseAi.probability }}%)
    </div>

    <div class="ai-disease-tags ai-disease-tags__species">
      <div class="tag" v-for="sp in species" :key="sp">
        <i :class="`mdi ${getPetIcon(sp)}`">{{sp}}</i>
      </div>
    </div>
    <div v-if="considerations" class="ai-disease-considerations">
      <b>Indícios compatíveis:</b>
      <ul>
        <li v-for="(line, index) in considerations.pros" :key="index">{{ line }}
        </li>
      </ul>
      <b>Fatores contra:</b>
      <ul>
        <li v-for="(line, index) in considerations.cons" :key="index">{{ line }}
        </li>
      </ul>
      <b>Para confirmar com mais precisão:</b>
      <ul>
        <li v-for="(line, index) in considerations.procedures" :key="index">
          {{ line }}
        </li>
      </ul>
    </div>
    <div class="ai-disease-tags ai-disease-tags__labels">
      <div :class='`tag ${state ? "danger" : "secondary"}`' v-if="labels"
        v-for="([label, state]) in labels" :key="label" style="font-size: 12px">
        {{ diseaseLabels[label][state] }}
      </div>
      <div class="bottom-buttons">
        <button class="icon-button deselect-button" v-if="selected && editable"
          @click='$emit("deselect")'>
          <i class="mdi mdi-cancel mdi-18px"></i>
        </button>
        <button class="icon-button" @click.stop="showDisease = true"><i
            class="mdi mdi-open-in-new mdi-18px"></i></button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  DISEASE_LABELS,
  getDiseaseLabels,
  getDiseaseSpecies,
  getPetIcon,
} from "./diseaseUtils";

export default {
  props: {
    selected: {
      type: Boolean,
      default: false,
    },
    diseaseAi: {
      type: Object,
      required: true,
    },
    editable: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      showDisease: false,
      diseaseLabels: DISEASE_LABELS,
    };
  },

  computed: {
    probabilidade() {
      if (this.diseaseAi.probability > 80) return "Alta";
      if (this.diseaseAi.probability > 60) return "Provável";
      if (this.diseaseAi.probability > 40) return "Média";
      if (this.diseaseAi.probability > 20) return "Baixa";
      return "Improvável";
    },

    labels() {
      return getDiseaseLabels(this.diseaseAi.disease.labels);
    },

    species() {
      return getDiseaseSpecies(this.diseaseAi.disease.species);
    },

    considerations() {
      const considerations = this.diseaseAi.considerations;
      if (!considerations) return null;

      try {
        const pros = this.parseConsiderations(considerations.pros);
        const cons = this.parseConsiderations(considerations.cons);
        const procedures = this.parseConsiderations(considerations.procedures);

        return { pros, cons, procedures };
      } catch {
        return null;
      }
    },
  },

  methods: {
    getPetIcon,
    parseConsiderations(value) {
      if (Array.isArray(value)) return value;
      if (!value || typeof value !== "string") throw new TypeError();

      const parsed = JSON.parse(value);
      if (!Array.isArray(parsed)) throw new TypeError();
      return parsed;
    },
  },
};
</script>

<style scoped>
.ai-disease {
  flex: 1;
  display: flex;
  flex-direction: column;
  font-size: 12px;

  --accent-1: #fffefa;
  --accent-3: #fffbeb;
  --accent-5: #fff3c2;
  --accent-7: #ffeb99;
  --accent-8: #d8bd4d;
  --accent-9: #927c22;
}

.disease-name {
  display: block;
  font-size: 14px;
  margin-bottom: 1em;
}

.selected-disease-name {
  font-weight: 700;
}

.ai-disease-tags {
  gap: 4px;
  display: flex;
  flex-wrap: wrap;
}

.ai-disease-considerations {
  margin-top: 1em;
  max-height: 400px;
  overflow-y: auto;
}

.ai-disease-considerations strong {
  margin-bottom: 0.25em;
}

.ai-disease-considerations ul {
  margin-bottom: 1em;
}

.ai-disease-tags__species .tag {
  font-weight: 500;
}

.ai-disease-tags__species i::before {
  font-size: 1.125em;
  margin-right: 4px;
}

.ai-disease-tags__labels {
  margin-top: auto;
}

.ai-disease-tags__labels .tag {
  height: fit-content;
}

.ai-disease-tags__labels .tag.danger {
  font-weight: 500;
}

.ai-disease-tags__labels .bottom-buttons {
  margin-top: 0.5em;
  margin-left: auto;
  display: flex;
  justify-content: end;
  gap: 0.5em;
}

.icon-button {
  padding: 0 5px;

  i {
    margin-left: 1px;
  }
}

.tag {
  color: #085d75;
  background-color: #cfe9f1;
  width: fit-content;
  padding: 4px 8px;
  border-radius: 8px;
  border: 1px solid currentColor;
}

.tag.secondary {
  color: #345e68;
  background-color: #ebeff0;
}

.tag.danger {
  color: #991b1b;
  background-color: #fee2e2;
}

.probability {
  margin-bottom: 8px;
}

.probability[data-probability="Alta"] {
  color: #ffffff;
  font-weight: 600;
  background-color: var(--accent-8);
}

.probability[data-probability="Provável"] {
  font-weight: 500;
  color: var(--accent-9);
  background-color: var(--accent-7);
}

.probability[data-probability="Média"] {
  font-weight: 500;
  color: var(--accent-9);
  background-color: var(--accent-5);
}

.probability[data-probability="Baixa"] {
  color: var(--accent-9);
  background-color: var(--accent-3);
}

.probability[data-probability="Improvável"] {
  color: var(--accent-9);
  background-color: var(--accent-1);
}
</style>